import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import CardContainer from '../../../components/cards/layout/CardContainer';
import CardGrid from '../../../components/cards/layout/CardGrid';
import PictureNameCard from '../../../components/cards/PictureNameCards/PictureNameCard';
import {
  requestTypesIcons,
  requestTypesStrings,
} from '../../../components/special/requestTypes';
import { useTranslation } from 'react-i18next';
import DateTimeLabel from '../../../components/label/DateTimeLabel';
import DateLabel from '../../../components/label/DateLabel';
import MoneyLabel from '../../../components/label/MoneyLabel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HotelIcon from '@mui/icons-material/Hotel';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { tokens } from '../../../global/theme/tokens';
import { useConfig } from '../../../api/api';
import { ExoFilePreview } from '../../../components/exo/ExoFilePreview';
import { numberToString } from '../../../components/special/numberConverter';
import { meansOfTransportationOptions } from '../NewTravelExpense';
import { MeansOfTransportationIcon } from '../MeansOfTransportationIcon';
import { travelExpenseCostCategories } from '../../../components/form/form-fields/TypeTravelExpenseCosts';

function labelFor(options, value) {
  return options.find((option) => option.value === value)?.label || value || '';
}

const RATE_LABELS = {
  full: 'Full day',
  partial: 'Arrival / departure day',
  none: 'No allowance',
};

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  declined: 'Declined',
};

const MEALS = [
  { key: 'breakfastProvided', label: 'Breakfast' },
  { key: 'lunchProvided', label: 'Lunch' },
  { key: 'dinnerProvided', label: 'Dinner' },
];

/**
 * A travel expense request points at a real trip. Requests recorded before the
 * travel log existed only carry the flat json_values snapshot; they fall back to
 * a simple key/value rendering.
 */
function tripOf(request) {
  return request.travelExpense || null;
}

export const TravelExpenseRequestHeader = ({ request }) => {
  const { t } = useTranslation();
  const trip = tripOf(request);
  const startTime = trip?.startTime ?? request.values?.startTime;

  return (
    <Box className="flex flex-col items-center gap-1">
      <Typography
        variant="h3"
        className="flex items-center justify-center gap-2"
      >
        {requestTypesIcons[request.type]}
        {t(requestTypesStrings[request.type])}
      </Typography>
      <Typography variant="h6" component="div">
        <DateTimeLabel dateTime={startTime} />
        {trip?.endTime && (
          <>
            {' – '}
            <DateTimeLabel dateTime={trip.endTime} />
          </>
        )}
      </Typography>
      {trip?.status && (
        <Chip size="small" label={t(STATUS_LABELS[trip.status] || trip.status)} />
      )}
    </Box>
  );
};

/** The read-only sibling of the form's card sections: title, optional extra
 *  header content and an amount badge over a card body. */
const DetailCard = ({ label, badge, extra, colors, className, children }) => (
  <Box
    className="w-full rounded-xl overflow-hidden flex flex-col"
    sx={{
      backgroundColor: colors.card,
      border: `1px solid ${colors.selected}`,
    }}
  >
    <Box
      className="flex items-center gap-3 px-4 py-2 flex-wrap"
      sx={{ borderBottom: `1px solid ${colors.selected}` }}
    >
      <Typography variant="h6" className="whitespace-nowrap">
        {label}
      </Typography>
      {extra}
      {badge != null && (
        <Chip
          size="small"
          className="ml-auto monospace"
          label={badge}
          sx={{ bgcolor: colors.bgInput }}
        />
      )}
    </Box>
    <Box className={className || 'flex flex-col gap-2 p-4'}>{children}</Box>
  </Box>
);

// Same idea as the form's travel log table: one row per leg under a shared
// column header. Text-only, so the columns are narrower than the form's.
const LEGS_GRID = {
  display: 'grid',
  gridTemplateColumns:
    '18px minmax(140px, 1.4fr) 120px 120px minmax(110px, 1fr) 56px 64px 76px',
  gap: '8px',
  alignItems: 'center',
};

const HeaderCell = ({ children, align }) => (
  <Typography
    component="span"
    className="uppercase truncate"
    sx={{
      fontSize: '10.5px',
      fontWeight: 600,
      letterSpacing: '0.05em',
      opacity: 0.6,
      textAlign: align || 'left',
    }}
  >
    {children}
  </Typography>
);

const LegsTable = ({ legs, colors, currencyPerKm, t }) => (
  <Box className="overflow-x-auto">
    <Box className="flex flex-col px-3 pb-2" sx={{ minWidth: '700px' }}>
      <Box sx={LEGS_GRID} className="pt-2 pb-1">
        <span />
        <HeaderCell>{`${t('From')} → ${t('To')}`}</HeaderCell>
        <HeaderCell>{t('Departure')}</HeaderCell>
        <HeaderCell>{t('Arrival')}</HeaderCell>
        <HeaderCell>{t('Means of Transportation')}</HeaderCell>
        <HeaderCell align="right">km</HeaderCell>
        <HeaderCell align="right">{currencyPerKm}</HeaderCell>
        <HeaderCell align="right">{t('Leg amount')}</HeaderCell>
      </Box>

      {legs.map((leg, index) => (
        <Box key={leg.id} sx={LEGS_GRID} className="py-1.5">
          {leg.isReturnLeg ? (
            <Tooltip title={t('Return')}>
              <SwapVertIcon sx={{ fontSize: '15px' }} className="opacity-60" />
            </Tooltip>
          ) : (
            <Typography
              component="span"
              variant="caption"
              className="opacity-60 font-bold"
            >
              {index + 1}
            </Typography>
          )}

          <Typography
            component="span"
            className="truncate"
            sx={{ fontSize: '13px', fontWeight: 500 }}
          >
            {[leg.fromLocation || '—', leg.toLocation || '—'].join(' → ')}
          </Typography>

          <Typography
            component="span"
            className="whitespace-nowrap opacity-80"
            sx={{ fontSize: '13px' }}
          >
            <DateTimeLabel dateTime={leg.departedAt} />
          </Typography>
          <Typography
            component="span"
            className="whitespace-nowrap opacity-80"
            sx={{ fontSize: '13px' }}
          >
            {leg.arrivedAt ? <DateTimeLabel dateTime={leg.arrivedAt} /> : '—'}
          </Typography>

          <Box className="flex items-center gap-1 min-w-0 opacity-80">
            <MeansOfTransportationIcon value={leg.meansOfTransportation} />
            <Typography
              component="span"
              className="truncate"
              sx={{ fontSize: '13px' }}
            >
              {t(labelFor(meansOfTransportationOptions, leg.meansOfTransportation))}
            </Typography>
          </Box>

          <Typography
            component="span"
            className="monospace whitespace-nowrap"
            sx={{ fontSize: '13px', textAlign: 'right' }}
          >
            {leg.distanceKilometers
              ? numberToString(leg.distanceKilometers, 2)
              : '—'}
          </Typography>
          <Typography
            component="span"
            className="monospace whitespace-nowrap opacity-60"
            sx={{ fontSize: '13px', textAlign: 'right' }}
          >
            {leg.pricePerKilometer
              ? numberToString(leg.pricePerKilometer, 2)
              : '—'}
          </Typography>
          <Typography
            component="span"
            className="monospace whitespace-nowrap"
            sx={{ fontSize: '13px', textAlign: 'right' }}
          >
            <MoneyLabel money={leg.amount} />
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

/** One compact line per allowance day, matching the form's day rows. */
const DayLine = ({ day, colors, first, t }) => {
  const providedMeals = MEALS.filter((meal) => day[meal.key]);

  return (
    <Box
      className="flex flex-wrap items-center gap-2 px-3 py-2"
      sx={first ? {} : { borderTop: `1px solid ${colors.selected}` }}
    >
      <Typography
        component="div"
        sx={{ fontSize: '13px', fontWeight: 500, width: '90px' }}
      >
        <DateLabel date={day.date} />
      </Typography>
      <Typography
        variant="caption"
        className="opacity-60"
        sx={{ width: '160px' }}
      >
        {t(RATE_LABELS[day.rateType] || RATE_LABELS.full)}
      </Typography>

      <Box className="flex flex-wrap items-center gap-1.5">
        {providedMeals.map((meal) => (
          <Chip key={meal.key} size="small" color="info" label={t(meal.label)} />
        ))}
        {day.overnightFlatRateClaimed && (
          <Tooltip title={t('Accommodation allowance')}>
            <HotelIcon color="success" sx={{ fontSize: '18px' }} />
          </Tooltip>
        )}
      </Box>

      <Typography
        className="monospace whitespace-nowrap ml-auto"
        sx={{ fontSize: '13px' }}
        component="div"
      >
        <MoneyLabel money={day.dayTotal} />
      </Typography>
    </Box>
  );
};

/** One read-only card per additional cost, with the receipt to verify it.
 *  A cost without its receipt is the reviewer's problem, so the card itself
 *  carries the warning. */
const CostCard = ({ cost, colors, onPreview }) => {
  const { t } = useTranslation();
  const file = cost.file;

  return (
    <Box
      className="flex flex-col gap-1 p-3 rounded-lg w-full"
      sx={{
        backgroundColor: colors.bgInput,
        border: `1px solid ${file ? colors.selected : colors.warning + '80'}`,
      }}
    >
      <Box className="flex items-center gap-2 flex-wrap">
        <Typography className="font-medium">
          {t(labelFor(travelExpenseCostCategories, cost.category))}
        </Typography>
        <Typography variant="body2" className="opacity-60" component="div">
          <DateLabel date={cost.incurredOn} />
        </Typography>
        <Typography className="monospace ml-auto font-medium" component="div">
          <MoneyLabel money={cost.grossAmount} />
        </Typography>
      </Box>

      {cost.description && (
        <Typography variant="body2" className="opacity-80">
          {cost.description}
        </Typography>
      )}

      <Box className="flex items-center gap-2 flex-wrap">
        <Typography
          variant="body2"
          className="opacity-60 monospace"
          component="div"
        >
          {`${t('Net amount')}: `}
          <MoneyLabel money={cost.netAmount} />
          {` · ${t('Tax rate')} ${Number(cost.taxRate)} %`}
        </Typography>

        {!file && (
          <Chip
            className="ml-auto"
            size="small"
            color="warning"
            icon={<ReceiptLongIcon />}
            label={t('Receipt missing')}
          />
        )}

        {file && (
          <Box
            className="flex items-center gap-1 rounded-md pl-2 pr-1 py-0.5 ml-auto"
            sx={{
              border: `1px solid ${colors.selected}`,
              backgroundColor: colors.card,
            }}
          >
            <ReceiptLongIcon fontSize="small" className="opacity-60" />
            <Tooltip title={file.title || t('Receipt')}>
              <Typography
                variant="body2"
                className="truncate"
                sx={{ maxWidth: '140px' }}
              >
                {file.title || t('Receipt')}
              </Typography>
            </Tooltip>
            <Tooltip title={t('Preview')}>
              <IconButton size="small" onClick={() => onPreview(file)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('Download')}>
              <IconButton
                size="small"
                component="a"
                href={file.temporaryUrl}
                target="_blank"
                rel="noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** The pre-travel-log rendering, kept for requests recorded before this feature. */
const LegacyDetails = ({ values }) => {
  const { t } = useTranslation();

  const rows = [
    {
      label: t('Means of Transportation'),
      value: t(labelFor(meansOfTransportationOptions, values.meansOfTransportation)),
    },
    { label: t('Distance (km)'), value: values.distanceKilometers },
    { label: t('Round trip'), value: values.isTwoWay ? t('Yes') : t('No') },
    { label: t('Total'), value: <MoneyLabel money={values.totalPrice} /> },
  ];

  return (
    <Box className="flex flex-col gap-1 w-full max-w-md mx-auto">
      {rows.map((row) => (
        <Box key={row.label} className="flex flex-row justify-between gap-4">
          <Typography variant="h6">{row.label}</Typography>
          <Typography variant="h6" component="div">
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// A checkbox value may arrive as the string '0'/'1'; '0' is truthy as a string.
function parseCheckbox(value) {
  if (typeof value === 'boolean') return value;
  if (value === undefined || value === null || value === '') return false;
  return Boolean(parseInt(value));
}

/**
 * The full trip, laid out like the form that created it (design "1a"): a
 * column of cards — the travel log as a dense table, the meal allowance as
 * compact day lines, additional costs and comment — next to a summary card
 * with the cost breakdown and total. Receipts can be previewed and downloaded
 * for verification.
 */
export const TravelExpenseRequestDetails = ({ request }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const currencySymbol = config?.currency?.currencySymbol ?? '';
  const trip = tripOf(request);

  const [previewFile, setPreviewFile] = useState(null);

  if (!trip) {
    return (
      <Box className="w-full flex flex-col gap-2">
        <LegacyDetails values={request.values || {}} />
        {request.comment && (
          <Typography className="text-center opacity-80">
            {request.comment}
          </Typography>
        )}
      </Box>
    );
  }

  const legs = trip.legs || [];
  const days = trip.days || [];
  const costs = trip.costs || [];

  const summaryRows = [
    {
      label: `${t('Mileage allowance')} · ${legs.length} ${t(legs.length === 1 ? 'Leg' : 'Legs')}`,
      value: trip.mileageTotal,
    },
    {
      label: `${t('Meal allowance')} · ${days.length} ${t(days.length === 1 ? 'Day' : 'Days')}`,
      value: trip.perDiemTotal,
    },
    ...((trip.overnightTotal ?? 0) > 0
      ? [{ label: t('Accommodation allowance'), value: trip.overnightTotal }]
      : []),
    { label: t('Additional costs'), value: trip.costsTotal },
  ];

  return (
    <Box className="w-full flex flex-col gap-4">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 280px' },
          gap: '16px',
          alignItems: 'start',
        }}
      >
        <Box className="flex flex-col gap-4 min-w-0">
          <DetailCard
            label={t('Travel Log')}
            badge={<MoneyLabel money={trip.mileageTotal} />}
            colors={colors}
            className="flex flex-col"
          >
            {legs.length === 0 ? (
              <Typography variant="body2" className="opacity-60 px-3 py-2">
                {t('No data available')}
              </Typography>
            ) : (
              <LegsTable
                legs={legs}
                colors={colors}
                currencyPerKm={`${currencySymbol}/km`}
                t={t}
              />
            )}
          </DetailCard>

          {days.length > 0 && (
            <DetailCard
              label={t('Meal allowance')}
              badge={
                <MoneyLabel
                  money={(trip.perDiemTotal ?? 0) + (trip.overnightTotal ?? 0)}
                />
              }
              extra={
                parseCheckbox(trip.hasOvernightStay) ? (
                  <Typography variant="caption" className="opacity-60">
                    {`${t('Overnight stay away from home')} ✓`}
                  </Typography>
                ) : null
              }
              colors={colors}
              className="flex flex-col"
            >
              {days.map((day, index) => (
                <DayLine
                  key={day.id ?? day.date}
                  day={day}
                  colors={colors}
                  first={index === 0}
                  t={t}
                />
              ))}
            </DetailCard>
          )}

          {costs.length > 0 && (
            <DetailCard
              label={t('Additional costs')}
              badge={<MoneyLabel money={trip.costsTotal} />}
              colors={colors}
            >
              {costs.map((cost) => (
                <CostCard
                  key={cost.id}
                  cost={cost}
                  colors={colors}
                  onPreview={setPreviewFile}
                />
              ))}
            </DetailCard>
          )}

          {request.comment && (
            <DetailCard label={t('Comment')} colors={colors}>
              <Typography variant="body2">{request.comment}</Typography>
            </DetailCard>
          )}
        </Box>

        {/* Summary, like the form's sidebar */}
        <Box
          className="flex flex-col gap-3 p-4 rounded-xl"
          sx={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.selected}`,
          }}
        >
          <Typography
            variant="caption"
            className="uppercase opacity-60"
            sx={{ fontWeight: 600, letterSpacing: '0.06em' }}
          >
            {t('Summary')}
          </Typography>

          {summaryRows.map((row) => (
            <Box key={row.label} className="flex justify-between items-baseline gap-2">
              <Typography variant="body2" className="opacity-60">
                {row.label}
              </Typography>
              <Typography
                variant="body2"
                className="monospace whitespace-nowrap"
                component="div"
              >
                <MoneyLabel money={row.value ?? 0} />
              </Typography>
            </Box>
          ))}

          <Box
            className="flex justify-between items-baseline gap-2 pt-3"
            sx={{ borderTop: `1px solid ${colors.selected}` }}
          >
            <Typography sx={{ fontWeight: 600 }}>{t('Total')}</Typography>
            <Typography
              variant="h4"
              className="monospace whitespace-nowrap"
              component="div"
            >
              <MoneyLabel money={trip.totalPrice} />
            </Typography>
          </Box>

          {trip.costCenter && (
            <Typography variant="caption" className="opacity-60">
              {`${t('Cost Center')}: ${trip.costCenter.name}`}
            </Typography>
          )}
        </Box>
      </Box>

      <ExoFilePreview
        open={Boolean(previewFile)}
        src={previewFile?.temporaryUrl}
        mimeType={previewFile?.mimeType || 'application/pdf'}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
};

export const ExtendTravelExpense = ({ request, onUpdate }) => {
  const { t } = useTranslation();

  return (
    <CardGrid>
      <CardContainer span={5}>
        <TravelExpenseRequestHeader request={request} />
      </CardContainer>
      <CardContainer span={5}>
        <TravelExpenseRequestDetails request={request} />
      </CardContainer>
      {request.processedBy && (
        <PictureNameCard
          data={request.processedBy}
          type="user"
          title={t('Processor')}
          pictureUrl={
            request.processedBy.picture
              ? request.processedBy.picture.temporaryUrl
              : ''
          }
          name={`${request.processedBy.firstName} ${request.processedBy.lastName}`}
          mail={request.processedBy.email}
          phone={request.processedBy.phone}
          position={request.processedBy.position}
        />
      )}
    </CardGrid>
  );
};

export default ExtendTravelExpense;
